import React, { useState } from "react";
import {Button, Dialog, DialogTitle, DialogContent, DialogContentText, TextField, DialogActions} from "@material-ui/core";
import { AssetCategoryType } from '../../../../entities/AssetType';
import { SelectTemplate } from "./SelectTemplate";
import { PremiumTemplatePreview } from "./PremiumTemplatePreview";
import { FieldValidationError, ValidationHelper } from "../../../../services/server/ServerResponses";
import { SermonTemplateService } from "../../../../services/SermonTemplateService";
import { BuilderAssetService, SaveBuilderAssetRequest } from "../../../../services/BuilderAssetService";
import { useLibrary, useLibraryAlert } from "../../LibraryPage";
import { UserAgent } from "../../../../utilities/UserAgent";
import { CustomTemplatePreview } from "./CustomTemplatePreview";
import { useHistory } from "react-router-dom";
import { BuilderAsset } from "../../../../entities/BuilderAsset";
import { SermonTemplate } from "../../../../entities/SermonTemplate";
import { routes } from "../../../../routes";

interface Props {
    open: boolean;
    onClose: () => void;
    selectedAssetType: AssetCategoryType;
}

export function AddFromTemplateDialog(props:Props) {
    const {open, selectedAssetType } = props;
    const context = useLibrary();
    const history = useHistory();
    const libraryAlert = useLibraryAlert();

    const [name, setName] = useState("");
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
    const [disabled, setDisabled] = useState(false);
    const [currentView, setCurrentView] = useState<"setName" | "setTemplate">("setName");
    const [selectedTemplate, setSelectedTemplate] = useState<SermonTemplate>();
    const [selectedBuilderAsset, setSelectedBuilderAsset] = useState<BuilderAsset>();

    const onAddFromPremiumTemplate = async () => {
        if(!selectedTemplate || name.length === 0)
            return;

		setDisabled(true);
		const response = await SermonTemplateService.activate(selectedTemplate.id, name, selectedAssetType.category);
		setDisabled(false);
        
        if (response.validation) return setErrors(response.errors);
        if (response.error) return libraryAlert(response.message);

        history.push(routes.app.resolve.builderPage(response.data), { prevUrl: window.location.href });
	};

    const onAddFromCustomeTemplate = async() => {    
        if(!selectedBuilderAsset || name.length === 0)
            return;

        const request: SaveBuilderAssetRequest = {
            parentId: context.activeFolderId ?? undefined,
            name: name,
            isStarred: false,
            dateAccessed: new Date(),
            category: selectedAssetType.category,
            text: selectedBuilderAsset.text,
            slideCount: selectedBuilderAsset.slideCount,
            isTemplate: false,
            templateDescription: "",
            slideExporterAspectRatio: selectedBuilderAsset.slideExporterAspectRatio,
            customSlideBackgroundImagePath: selectedBuilderAsset.customSlideBackgroundImagePath,
            offlineMode: false,
            lastModifiedFormFactor: UserAgent.getFormFactor(),
            lastModifiedOnOperatingSystem: UserAgent.getOperatingSystem(),
        };

        setDisabled(true);
        let response = await BuilderAssetService.saveFromTemplate(request);
        setDisabled(false);

        if (response.validation) return setErrors(response.errors);
        if (response.error) return libraryAlert(response.message);

        history.push(routes.app.resolve.builderPage(response.data.id), { prevUrl: window.location.href });
    }
    
    return (
        <Dialog
            open={open}
            onClose={props.onClose}
            disableBackdropClick={disabled}
            disableEscapeKeyDown={disabled}
            aria-labelledby="form-dialog-title"
            fullScreen={currentView === "setTemplate"}
        >
        { currentView === "setName" && 
            <SetAssetName 
                categoryName={props.selectedAssetType.name}
                name={name}
                setName={setName}
                onClose={props.onClose}
                onAdd={() => setCurrentView("setTemplate")}
                errors={errors} />
        }
        { currentView === "setTemplate" && 
            <SelectTemplate 
                category={props.selectedAssetType.category} 
                onClose={props.onClose}
                onSermonTemplateSelected={(template) => setSelectedTemplate(template)}
                onCustomTemplateSelected={(asset) => setSelectedBuilderAsset(asset)} />
        }
        {selectedBuilderAsset && 
            <CustomTemplatePreview 
                disabled={disabled}
                onClose={() => setSelectedBuilderAsset(undefined)}
                customTemplate={selectedBuilderAsset}
                onUseTemplate={onAddFromCustomeTemplate} />
        }
        {selectedTemplate && 
            <PremiumTemplatePreview 
                disabled={disabled}
                onClose={() => setSelectedTemplate(undefined)}
                sermonTemplate={selectedTemplate}
                onUseTemplate={onAddFromPremiumTemplate} />
        }
        </Dialog>
    );
}

interface setAssetNameProps {
    categoryName: string;
    name: string;
    setName: (updatedName:string) => void;
    onClose: () => void;
    onAdd: () => void;
    errors: FieldValidationError[];
}

function SetAssetName(props:setAssetNameProps){

    const handleSubmit = (e:React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        props.onAdd();
    }
    
    return (
        <form onSubmit={handleSubmit}>
            <DialogTitle id="form-dialog-title">New {props.categoryName}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Enter the name of your new {props.categoryName}.
                </DialogContentText>
                <TextField
                    required
                    autoFocus={true}
                    margin="dense"
                    id="newItemName"
                    label="Name"
                    type="text"
                    onChange={(e) => props.setName(e.currentTarget.value)}
                    value={props.name}
                    fullWidth
                    error={ValidationHelper.isFieldInError(props.errors, 'Name')}
                    helperText={ValidationHelper.getFieldErrorSummary(props.errors, 'Name')}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose} color="primary">
                    Cancel
                </Button>
                <Button type="submit" color="primary">
                    Add
                </Button>
            </DialogActions>
        </form>
    );
}
